'use client'

import Link from 'next/link'
import { ArrowRightIcon, ArrowUpRightIcon } from 'lucide-react'

import { cn } from '@/utilities/ui'

const blockLinkBaseClasses =
  'group/block-link flex flex-col gap-2 border border-border rounded-xl self-stretch w-full bg-white text-primary text-left hover:bg-accent hover:text-background hover:border-accent transition-colors duration-200 ease-in-out p-6 relative cursor-pointer h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-0'

type BlockLinkProps = {
  href: string | null
  title: string
  subtitle: string
  isInternalLink?: boolean
  newTab?: boolean
  className?: string
}

const InnerBlock = ({
  title,
  subtitle,
  isInternalLink = true,
}: Omit<BlockLinkProps, 'href' | 'className' | 'newTab'>) => {
  return (
    <>
      <h3 className="font-family-display text-2xl leading-8 font-normal">{title}</h3>
      <p className="line-clamp-3 max-w-75 overflow-hidden font-family-body text-xs leading-4 text-ellipsis">
        {subtitle}
      </p>
      <div
        className={cn(
          'absolute right-3 bottom-3 w-fit self-end rounded-full bg-border p-1 pt-1 text-foreground transition-colors duration-200 ease-in-out',
          'group-hover/block-link:bg-white group-hover/block-link:text-primary',
        )}
      >
        {isInternalLink ? (
          <ArrowRightIcon className="size-3 stroke-2 text-foreground" />
        ) : (
          <ArrowUpRightIcon className="size-3 stroke-2 text-foreground" />
        )}
      </div>
    </>
  )
}

/**
 * Reusable block-style link or button. Renders a Next.js Link when href is set,
 * otherwise a button. Use for card-style CTAs with consistent styling.
 */
export const BlockLink: React.FC<BlockLinkProps> = ({
  href,
  title,
  subtitle,
  isInternalLink = true,
  newTab = false,
  className,
}) => {
  if (href) {
    return (
      <Link
        href={href}
        className={cn(blockLinkBaseClasses, className)}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noopener noreferrer' : undefined}
      >
        <InnerBlock title={title} subtitle={subtitle} isInternalLink={isInternalLink} />
      </Link>
    )
  }

  return (
    <button type="button" className={cn(blockLinkBaseClasses, className)}>
      <InnerBlock title={title} subtitle={subtitle} isInternalLink={isInternalLink} />
    </button>
  )
}
